/* eslint-disable */
import {
  computed,
  inject,
  Inject,
  Injectable,
  signal,
  WritableSignal,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Ability } from "@casl/ability";
import { GlobalService } from "@core/services/global.service";
import { LocalService } from "@core/services/local-storage.service";
import { TranslateService } from "@ngx-translate/core";
import { RowSelection } from "@shared/datatable/components/enums";
import { CabTableRequest } from "@shared/requests/CabRequest";
import { isFloat } from "@shared/utils/math";
import {
  ColDef,
  ColGroupDef,
  GridApi,
  IRowNode,
  RowClassParams,
  RowStyle,
} from "ag-grid-community";
import { Observable, Subject } from "rxjs";
import { catchError } from "rxjs/operators";
import removeEmptyProperties from "../../shared/utils/removeEmptyProperties";
import {
  IConfirmationDialogModalOpen,
  IDefaultServiceActionHandler,
} from "../interfaces/default.interface";
import { ConfirmationDialogService } from "./confirm-modal.service";
import { FormModalService } from "./modal.service";
import { ToastService } from "./toast.service";
import { SessionStorageService } from "@core/services/session-storage.service";

@Injectable({
  providedIn: "root",
})
export abstract class DefaultService<T, Ti, Tu> extends GlobalService<
  T,
  Ti,
  Tu
> {
  _toastService = inject(ToastService);
  _ability = inject(Ability);
  _translateService = inject(TranslateService);
  _modalService = inject(FormModalService);
  _localStorageService = inject(LocalService);
  _sessionStorageService = inject(SessionStorageService);
  _confirmationDialogService = inject(ConfirmationDialogService);
  readonly selectedRow = signal<any>([]);
  readonly dbSelectedRow = signal<any>([]);
  readonly selectedItems = signal<IRowNode | any>([]);
  readonly iDKey = "id";
  readonly hasSelectedRow = computed(
    () => Object.keys(this.selectedRow()).length > 0
  );
  readonly hasDbSelectedRow = computed(
    () => Object.keys(this.dbSelectedRow()).length > 0
  );
  rowData = signal([]);
  hasFilter = signal(false);
  reloadColDefs: WritableSignal<number | boolean> = signal(1);
  totalPages = signal(0);
  tableActions = signal<RowSelection | undefined>(RowSelection.SINGLE);
  gridApi!: GridApi;
  form: FormGroup;
  clearTableParams = new Subject<boolean>();
  columnDefs: (ColDef | ColGroupDef)[] = [];
  pinnedBottomRowDataObj: any[] = [];
  calculatingInterest: any[] = [];
  totalPinnedBottomRowElements: any = {};
  totalPinnedBottomRowDataObj: any = {};
  pinnedBottomRowTotalTitleSetIn = "name";
  tableRequest: CabTableRequest;
  warningServices = ["VillagesService", "AreaBrickingService"];

  private readonly PREFIX_OF_CURRENT_SERVICE_KEY = "COPY_OF_";

  constructor(@Inject("url") public url: string) {
    super();
    if (this.url.startsWith("./")) this._api = this.url;
    else this._api += this.url;
    this.tableRequest = new CabTableRequest(this._api, {});
  }

  abstract getServiceKey(): string;

  get filter() {
    return (
      this.tableRequest.getFilterToLocaleStorage(this.getServiceKey()) ?? {}
    );
  }

  set filter(filterData: any) {
    this.tableRequest.setFilterToLocaleStorage(this.getServiceKey(), {
      ...this.tableRequest.defaultParams,
      ...removeEmptyProperties(filterData),
    });

    this.hasFilter.set(this.hasFilterParams());
  }

  hasFilterParams() {
    return (
      Object.keys(
        this.tableRequest.removeDefaultQueries(
          removeEmptyProperties(this.filter)
        )
      ).length > 0
    );
  }

  // hasFilterParams() {
  //   const initialFilterValue = this.tableRequest.removeDefaultQueries(
  //     removeEmptyProperties(this.getInitialFilterParams() ?? {})
  //   );
  //   const filterValue = this.tableRequest.removeDefaultQueries(
  //     removeEmptyProperties(this.filter)
  //   );
  //
  //   return JSON.stringify(initialFilterValue) !== JSON.stringify(filterValue);
  // }

  removeFilterFromLocaleStorage() {
    this.tableRequest.removeFilterFromLocaleStorage(this.getServiceKey());
  }

  copyInitialFilterParams() {
    const initialFilterValue = this.tableRequest.removeDefaultQueries(
      removeEmptyProperties(this.getInitialFilterParams() ?? {})
    );
    const filterValue = this.tableRequest.removeDefaultQueries(
      removeEmptyProperties(this.filter)
    );
    if (JSON.stringify(initialFilterValue) !== JSON.stringify(filterValue)) {
      return null;
    } else {
      const initialFilterValue = { ...this.filter };

      this.tableRequest.setFilterToLocaleStorage(
        this.PREFIX_OF_CURRENT_SERVICE_KEY + this.getServiceKey(),
        JSON.parse(JSON.stringify(initialFilterValue))
      );
    }
  }

  getInitialFilterParams() {
    return this.tableRequest.getFilterToLocaleStorage(
      this.PREFIX_OF_CURRENT_SERVICE_KEY + this.getServiceKey()
    );
  }

  addParamToFilter(params: object) {
    const param = { ...this.filter, ...params };
    this.filter = removeEmptyProperties(param);
  }

  setDefaultTableParams(params: object) {
    this.tableRequest.setDefaultParams(params);
  }

  selectRowData(rowData: any) {
    this.selectedRow.set(rowData);
  }

  dbSelectRowData(rowData: any) {
    this.dbSelectedRow.set(rowData);
  }

  setFilterTable(obj: any) {
    this.addParamToFilter(obj);
    this.loadDataTable();
  }

  reloadTable(): Observable<any> {
    return this._http.request(
      this.tableRequest.getMethod(),
      this.tableRequest.getUrl(),
      {
        params: removeEmptyProperties(this.filter),
      }
    );
  }

  confirmCheck(event: boolean, getKeyToSend: string = "id") {
    if (event) {
      const id = this.selectedRow()[getKeyToSend];

      this.subscriptions = this.delete(id)
        .pipe(
          catchError((err) => {
            this._toastService.error(err.error.message);
            return err;
          })
        )
        .subscribe({
          next: () => {
            this._toastService.success("TOAST.TOAST_DELETE");
            this.subscriptions.unsubscribe();
          },
          error: (err) => {
            console.error(err);
            this.subscriptions.unsubscribe();
          },
        });
    }
  }

  actionDelete(dialogOptions: IConfirmationDialogModalOpen = {}) {
    if (this.hasSelectedRow()) {
      const defaultOptions: IConfirmationDialogModalOpen = {
        ...{
          title: "BUTTON.DELETE",
          message: "CONFIRMATION.DELETE",
        },
        ...dialogOptions,
      };
      this._confirmationDialogService.open(defaultOptions).then((event) => {
        this.confirmCheck(event);
      });
      this.selectedItems.set([]);
    }
  }

  actionHandler(options: IDefaultServiceActionHandler) {
    const { event, actions, actionDeleteOptions = {} } = options;

    const hasSelectedRow = computed(() => this.hasSelectedRow());

    let action: null | string = null;
    const warningToast = () =>
      this._toastService.warning("TOAST.TOAST_SELECT_ROW");

    switch (event) {
      case actions.ADD:
        action = actions.ADD;
        break;
      case actions.EDIT:
        hasSelectedRow() ? (action = actions.EDIT) : warningToast();
        break;
      case actions.DELETE:
        action = actions.DELETE;
        this.actionDelete(actionDeleteOptions);
        break;
    }
    return action;
  }

  createFooterData(data: any) {
    if (!data) return [];
    const pinnedBottomRowDataObj = this.pinnedBottomRowDataObj;
    const result: any = {};

    const rowData = [
      this.pinnedBottomRowTotalTitleSetIn,
      ...pinnedBottomRowDataObj,
    ];

    rowData.forEach((rowDataKey) => {
      if (rowDataKey == this.pinnedBottomRowTotalTitleSetIn) {
        result[this.pinnedBottomRowTotalTitleSetIn] =
          this._translateService.instant("ACTION_DROPDOWN.TOTAL");
      } else {
        result[rowDataKey] = this.createFooterDataKey(rowDataKey, data);
      }
    });
    return [result];
  }

  createFooterDataKey(
    rowDataKey: string,
    data: any,
    asHtml = true
  ): string | number {
    let summation = 0;

    data?.forEach((item: any) => {
      if (!Number(item[rowDataKey])) return;

      summation += Number(item[rowDataKey]);
    });
    if (!asHtml) return summation;
    if (isFloat(summation)) {
      return summation
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    } else {
      return summation.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }
  }

  getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    // agarda bu fn override qilinsa shu tekshirishni yozish shart
    if (params.data === undefined) {
      return { opacity: 0 };
    }
    //
    return {};
  };

  clearStepperParamsOnDataTableClearButtonClick(): void {}

  partialSelect(node) {
    return node;
  }
}
